/**
 * The negotiator page's address: `?negotiator=penny` (one of the team's negotiators, by its key, cleaned as the server
 * makes keys: "Mary-Jo" is maryjo; `dress=1` opens its full robot view, as right after it was created; `view=changes`
 * opens its column on Every change) or `?new=1` (Create negotiator; Draper's link may fill in the name she
 * gave and the way she asked for: `name=Bolt&start=wilder`). Plain checks, no zod: a route's validator ships in the entry
 * chunk.
 */
export interface BuilderSearch {
  negotiator?: string;
  dress?: boolean;
  view?: 'changes';
  new?: boolean;
  name?: string;
  start?: 'even' | 'penny' | 'wilder' | 'calco';
}

const KEY = /^[a-z][a-z0-9]{1,23}$/;
/** A name or key as the server makes a key of it: letters and digits only, lower case ("Mary-Jo" → maryjo). */
export const keyFrom = (text: string): string => text.normalize('NFKD').toLowerCase().replace(/[^a-z0-9]/g, '');
const STARTS = ['even', 'penny', 'wilder', 'calco'] as const;
const yes = (v: unknown): boolean => v === true || v === 1 || v === '1' || v === 'true';

export function parseBuilderSearch(search: { negotiator?: unknown; dress?: unknown; view?: unknown; new?: unknown; name?: unknown; start?: unknown }): BuilderSearch {
  const cleaned = typeof search.negotiator === 'string' ? keyFrom(search.negotiator) : '';
  const key = KEY.test(cleaned) ? cleaned : undefined;
  if (key) return { negotiator: key, ...(yes(search.dress) ? { dress: true } : {}), ...(search.view === 'changes' ? { view: 'changes' as const } : {}) };
  if (!yes(search.new)) return {};
  // A name Draper left as the link's own placeholder ("<its name>") is no name.
  const name = typeof search.name === 'string' && !/[<>]/.test(search.name) ? search.name.replace(/\s+/g, ' ').trim().slice(0, 24) : '';
  const start = STARTS.find((s) => s === search.start);
  return { new: true, ...(name ? { name } : {}), ...(start ? { start } : {}) };
}

/**
 * Her first words to a negotiator she just made, handed from Create to its page once. Never in the address: a link
 * anyone can open must never send a message as whoever opens it.
 */
const firstWords = new Map<string, string>();
export const handFirstWords = (key: string, words: string): void => void firstWords.set(key, words);
export function takeFirstWords(key: string): string | undefined {
  const words = firstWords.get(key);
  firstWords.delete(key);
  return words;
}
